import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { AuthState, User } from "../types";
import AuthService from "../services/AuthService";
import LegalService from "../services/LegalService";

// Default auth state
const defaultAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  hasAcceptedLatestTerms: false,
  hasAcceptedLatestPrivacy: false,
};

// Create auth context
const AuthContext = createContext<{
  authState: AuthState;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  acceptTerms: (version: string) => Promise<void>;
  acceptPrivacy: (version: string) => Promise<void>;
  handleLoginCallback: () => Promise<User | void>;
  refreshToken: () => Promise<User | null>;
}>({
  authState: defaultAuthState,
  login: async () => {},
  logout: async () => {},
  acceptTerms: async () => {},
  acceptPrivacy: async () => {},
  handleLoginCallback: async () => {},
  refreshToken: async () => null,
});

// Auth context provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log("Initializing auth context");
        // Initialize the auth service
        AuthService.init();

        const isAuthenticated = await AuthService.isAuthenticated();
        console.log("Is authenticated:", isAuthenticated);

        if (isAuthenticated) {
          const user = await AuthService.getCurrentUser();
          const accessToken = await AuthService.getAccessToken();
          console.log("User retrieved:", user ? "Yes" : "No");

          if (user) {
            const { termsAccepted, privacyAccepted } =
              await LegalService.checkUserAcceptance(user);
            console.log(
              "Terms accepted:",
              termsAccepted,
              "Privacy accepted:",
              privacyAccepted
            );

            setAuthState({
              isAuthenticated,
              user,
              accessToken,
              hasAcceptedLatestTerms: termsAccepted,
              hasAcceptedLatestPrivacy: privacyAccepted,
            });
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // Login handler
  const login = async () => {
    await AuthService.loginWithRedirect();
  };

  // Logout handler
  const logout = async () => {
    await AuthService.logout();
    setAuthState(defaultAuthState);
  };

  // Handle login callback
  const handleLoginCallback = async () => {
    try {
      console.log("AuthContext: Processing login callback");
      const user = await AuthService.handleLoginCallback();

      if (!user) {
        console.warn("AuthContext: No user returned from handleLoginCallback");
        throw new Error("Authentication failed - no user returned");
      }

      // Get the access token
      const accessToken = await AuthService.getAccessToken();
      console.log(
        "AuthContext: Retrieved access token",
        accessToken ? "successfully" : "failed"
      );

      // Check legal acceptance status
      const { termsAccepted, privacyAccepted } =
        await LegalService.checkUserAcceptance(user);

      console.log(
        "AuthContext: User legal status - Terms:",
        termsAccepted ? "Accepted" : "Not accepted",
        "Privacy:",
        privacyAccepted ? "Accepted" : "Not accepted"
      );

      // Update auth state with the new user information
      setAuthState({
        isAuthenticated: true,
        user,
        accessToken,
        hasAcceptedLatestTerms: termsAccepted,
        hasAcceptedLatestPrivacy: privacyAccepted,
      });

      console.log("AuthContext: Auth state updated successfully after login");
      return user;
    } catch (error) {
      console.error("AuthContext: Error handling login callback:", error);
      setAuthState(defaultAuthState); // Reset to unauthenticated state on error
      throw error;
    }
  };

  // Accept terms and conditions
  const acceptTerms = async (version: string) => {
    if (!authState.user) return;

    try {
      await LegalService.acceptTerms(authState.user.id, version);
      setAuthState((prev) => ({
        ...prev,
        hasAcceptedLatestTerms: true,
        user: prev.user
          ? { ...prev.user, acceptedTerms: true, termsVersion: version }
          : null,
      }));
    } catch (error) {
      console.error("Error accepting terms:", error);
      throw error;
    }
  };

  // Accept privacy policy
  const acceptPrivacy = async (version: string) => {
    if (!authState.user) return;

    try {
      await LegalService.acceptPrivacy(authState.user.id, version);
      setAuthState((prev) => ({
        ...prev,
        hasAcceptedLatestPrivacy: true,
        user: prev.user
          ? { ...prev.user, acceptedPrivacy: true, privacyVersion: version }
          : null,
      }));
    } catch (error) {
      console.error("Error accepting privacy policy:", error);
      throw error;
    }
  };

  // Refresh token
  const refreshToken = async () => {
    try {
      console.log("AuthContext: Attempting to refresh token");
      const user = await AuthService.refreshToken();

      if (!user) {
        console.warn("AuthContext: No user returned from refreshToken");
        return null;
      }

      // Get the new access token
      const accessToken = await AuthService.getAccessToken();
      console.log("AuthContext: Retrieved new access token");

      // Update auth state with refreshed user information
      setAuthState((prevState) => ({
        ...prevState,
        isAuthenticated: true,
        user,
        accessToken,
      }));

      console.log("AuthContext: Auth state updated after token refresh");
      return user;
    } catch (error) {
      console.error("AuthContext: Error refreshing token:", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        login,
        logout,
        acceptTerms,
        acceptPrivacy,
        handleLoginCallback,
        refreshToken,
      }}
    >
      {isInitialized ? children : <div>Loading...</div>}
    </AuthContext.Provider>
  );
};

// Hook for using auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;
